// SMS API service functions
export interface SMSMessage {
  id: string
  lead_phone: string
  lead_name: string
  direction: 'inbound' | 'outbound'
  body: string
  status: string
  sent_by_name?: string
  created_at: string
}

export interface SMSConversationResponse {
  error: boolean
  lead_id: string
  lead_name: string
  lead_phone: string
  count: number
  messages: SMSMessage[]
}

export interface SendSMSRequest {
  lead_id: string
  message_body: string
}

export interface SendSMSResponse {
  error: boolean
  message: string
  data?: {
    id: string
    message_sid: string
    lead_phone: string
    direction: string
    body: string
    status: string
    created_at: string
  }
}

export const smsApi = {
  // Get SMS conversation for a lead
  async getConversation(leadId: string): Promise<SMSConversationResponse> {
    const response = await fetch(`/api/sms/conversation/lead/${leadId}/`, {
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  },

  // Send SMS to a lead
  async sendSms(data: SendSMSRequest): Promise<SendSMSResponse> {
    const response = await fetch('/api/sms/send/', {
      method: 'POST',
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  // Get SMS conversation by phone number
  async getConversationByPhone(phoneNumber: string): Promise<SMSConversationResponse> {
    const response = await fetch(`/api/sms/conversation/phone/${phoneNumber}/`, {
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  },

  // Get unread SMS counts per lead
  async getUnreadCounts(): Promise<{
    error: boolean
    total_unread: number
    leads: Array<{ lead_id: string; unread_count: number }>
  }> {
    const response = await fetch('/api/sms/unread-count/', {
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  },

  // Mark messages as read for a specific lead
  async markMessagesRead(leadId: string): Promise<{
    error: boolean
    message: string
    marked_read: number
  }> {
    const response = await fetch(`/api/sms/mark-read/${leadId}/`, {
      method: 'POST',
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }
}